import { Link } from "react-router-dom";
import {
  Archive, ArrowUpRight, Building2, CalendarRange, Camera, CheckCircle2, Clock3, Coffee, FileText, MapPin, Send,
  Smartphone, StickyNote, Users,
} from "lucide-react";
import { Reveal, SectionHead } from "@/components/home/Section";
import { SEO } from "@/seo/useSeo";

const ICONS = { archive: Archive, building: Building2, calendar: CalendarRange, camera: Camera, check: CheckCircle2, clock: Clock3,
  coffee: Coffee, file: FileText, pin: MapPin, send: Send, smartphone: Smartphone, note: StickyNote, users: Users };

export const LandingProblem = ({ page }) => (
  <section className="bg-white py-24 lg:py-28" data-testid="landing-problem">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
      <SectionHead num="01" eyebrow={page.problem.eyebrow} title={page.problem.h2} />
      <Reveal delay={0.1}><p className="text-lg md:text-xl leading-relaxed text-slate-700 lg:pt-10">{page.problem.p}</p></Reveal>
    </div>
  </section>
);

export const LandingSteps = ({ page }) => (
  <section className="bg-[#F8FAFC] home-grid-light py-24 lg:py-28" data-testid="landing-steps">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHead num="02" eyebrow="How it works" title={page.steps.h2} />
      <ol className="mt-14 grid md:grid-cols-3 gap-5">
        {page.steps.items.map((s, i) => (
          <Reveal key={s.t} delay={0.08 * i}>
            <li className="h-full rounded-2xl bg-white border border-slate-200 p-7 sm:p-8" data-testid={`landing-step-${i}`}>
              <span className="font-mono text-4xl font-bold text-[#EA580C]">0{i + 1}</span>
              <h3 className="font-display mt-5 text-xl font-bold tracking-tight">{s.t}</h3>
              <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">{s.p}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export const LandingFeatures = ({ page }) => (
  <section className="bg-[#0B1120] text-white py-24 lg:py-28 home-noise" data-testid="landing-features">
    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHead num="03" eyebrow="Features" title={page.features.h2} dark />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
        {page.features.items.map((f, i) => {
          const Icon = ICONS[f.icon];
          return (
            <div key={f.t} className="bg-[#0B1120] p-7 sm:p-8 hover:bg-[#111A2E] transition-colors" data-testid={`landing-feature-${i}`}>
              <span className="w-11 h-11 rounded-xl bg-[#EA580C]/15 text-[#EA580C] grid place-items-center"><Icon className="w-5 h-5" /></span>
              <h3 className="font-display mt-5 text-lg font-bold">{f.t}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.p}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export const LandingRelated = ({ page }) => (
  <section className="bg-white py-20" data-testid="landing-related">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <p className="font-mono text-xs font-semibold tracking-[0.2em] uppercase text-[#EA580C]">More from Cross Jobs</p>
      <div className="mt-6 grid md:grid-cols-3 gap-5">
        {[...page.related, "/"].map((path) => {
          const p = SEO.pages[path];
          return (
            <Link key={path} to={path === "/" ? "/home" : path} data-testid={`landing-related-${p.slug}`}
              className="group rounded-2xl border border-slate-200 p-6 hover:border-[#EA580C] hover:-translate-y-0.5 transition-[transform,border-color]">
              <p className="font-display text-lg font-bold flex items-center justify-between gap-3">{p.label}
                <ArrowUpRight className="w-5 h-5 text-[#EA580C] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></p>
              <p className="mt-2 text-sm text-slate-600">{p.description}</p>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);
